import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import EnhancedCreateWODialog from '../components/EnhancedCreateWODialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { NettStatusTable } from '../components/ui/nett-status-table';
import { formatQuantity as formatNumber } from '@/utils/formatters';
import { 
  Factory, Package, AlertTriangle, ChevronDown, ChevronUp, 
  RefreshCw, Loader2, Calendar, Box, ClipboardList, TrendingDown, Zap, Link2, Info, Search
} from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../components/ui/popover';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductionFulfillmentPage = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ 
    total_items: 0, fng_count: 0, sfg_count: 0, wo_bom_sourced_count: 0, bom_pending_count: 0, total_shortage: 0 
  });
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItem, setExpandedItem] = useState(null); // Single expanded item (accordion style)
  const [liveNettStatus, setLiveNettStatus] = useState(null); // Live recalculated nett status
  const [loadingNettStatus, setLoadingNettStatus] = useState(false);
  
  // WO Creation state
  const [createWODialogOpen, setCreateWODialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [fifoPreview, setFifoPreview] = useState([]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchFulfillmentItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = categoryFilter !== 'all' ? `?category=${categoryFilter}` : '';
      const response = await axios.get(`${API_URL}/production-fulfillment${params}`, { 
        headers: getAuthHeader() 
      });
      setItems(response.data.items || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error fetching fulfillment items:', error);
      toast.error('Failed to load production fulfillment items');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => {
    fetchFulfillmentItems();
  }, [fetchFulfillmentItems]);

  const toggleItemExpand = async (itemId) => {
    if (expandedItem === itemId) {
      setExpandedItem(null);
      setLiveNettStatus(null);
      return;
    }
    setExpandedItem(itemId);
    setLiveNettStatus(null);
    setLoadingNettStatus(true);
    try {
      const response = await axios.get(`${API_URL}/items/${itemId}/nett-status`, { headers: getAuthHeader() });
      const data = response.data;
      const cur = data.stock_position?.current || {};
      setLiveNettStatus({
        stock_in_hand: cur.stock_in_hand || 0,
        wip: cur.wip || 0,
        ohwip: cur.ohwip || 0,
        indent_qty: cur.indent_qty || 0,
        po_issued: cur.po_issued || 0,
        blocked: cur.blocked || 0,
        so_blocked: cur.so_blocked || 0,
        nett: cur.nett_status || 0,
        free_stock: cur.free_stock || 0,
        wip_rmt: cur.wip_rmt || 0,
      });
    } catch (error) {
      console.error('Error fetching live nett status:', error);
    } finally {
      setLoadingNettStatus(false);
    }
  };

  // Calculate FIFO allocation preview when WO qty changes
  // Now includes stock allocation for remaining demand
  // Supports both SO-sourced and WO-sourced (SFG) items
  const calculateFifoPreview = (item, qty) => {
    if (!item || !qty || qty <= 0) {
      setFifoPreview([]);
      return;
    }
    
    let remainingWoQty = parseFloat(qty);
    let availableStock = item.nett_status?.stock_in_hand || 0;
    const preview = [];
    
    // Get all linked sources (could be SO items or WO BOM items)
    const linkedSources = item.linked_so_items || [];
    
    // Step 1: Allocate WO qty (FIFO by delivery date)
    for (const src of linkedSources) {
      const pendingQty = src.pending_qty || src.required_quantity || 0;
      const allocQty = Math.min(remainingWoQty, pendingQty);
      
      if (src.source_type === 'work_order') {
        // WO-sourced (SFG item)
        preview.push({
          source_type: 'work_order',
          work_order_number: src.work_order_number,
          parent_item_name: src.parent_item_name,
          delivery_date: src.delivery_date,
          so_qty: pendingQty,
          wo_allocated: allocQty,
          stock_allocated: 0
        });
      } else {
        // SO-sourced (default)
        preview.push({
          source_type: 'sales_order',
          so_number: src.so_number,
          so_line_code: src.so_line_code,  // SO line number e.g. SAMHY0138L-01
          customer_name: src.customer_name,
          delivery_date: src.delivery_date,
          so_qty: pendingQty,
          wo_allocated: allocQty,
          stock_allocated: 0
        });
      }
      remainingWoQty -= allocQty;
      if (remainingWoQty <= 0) break;
    }
    
    // Step 2: Allocate remaining stock to unfulfilled demand (continue FIFO)
    for (const p of preview) {
      if (availableStock <= 0) break;
      
      const remainingDemand = p.so_qty - p.wo_allocated;
      if (remainingDemand > 0) {
        const stockAlloc = Math.min(availableStock, remainingDemand);
        p.stock_allocated = stockAlloc;
        availableStock -= stockAlloc;
      }
    }
    
    setFifoPreview(preview);
  };

  const openCreateWODialog = (item) => {
    if (item.bom_status === 'pending') {
      toast.error('BOM is pending. Create BOM first before creating Work Order.');
      return;
    }
    setSelectedItem(item);
    calculateFifoPreview(item, item.shortage);
    setCreateWODialogOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Check if item has any WO BOM sources
  const hasWoBomSource = (item) => {
    return item.linked_so_items?.some(src => src.source_type === 'work_order');
  };

  // Get counts of SO vs WO sources
  const getSourceCounts = (item) => {
    const sources = item.linked_so_items || [];
    const woCount = sources.filter(s => s.source_type === 'work_order').length;
    const soCount = sources.length - woCount;
    return { soCount, woCount };
  };

  // Calculate days remaining until delivery
  const getDaysRemaining = (dateStr) => {
    if (!dateStr) return null;
    try {
      const deliveryDate = new Date(dateStr);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      deliveryDate.setHours(0, 0, 0, 0);
      const diffTime = deliveryDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  // SO Breakdown Popover Component
  const SOBreakdownPopover = ({ item }) => {
    const sources = item.linked_so_items || [];
    if (sources.length === 0) return null;

    return (
      <Popover>
        <PopoverTrigger asChild>
          <button 
            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-1.5 py-0.5 rounded transition-colors"
            onClick={(e) => e.stopPropagation()}
            data-testid={`so-breakdown-trigger-${item.item_id}`}
          >
            <Info className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">View breakdown</span>
          </button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-[500px] p-0" 
          align="start"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-3 bg-slate-50 border-b">
            <h4 className="font-semibold text-slate-800">SO Breakdown</h4>
            <p className="text-xs text-slate-500">
              Total demand: {formatNumber(item.make_demand || item.total_pending_qty)} across {sources.length} source(s)
            </p>
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-slate-600">SO Number</th>
                  <th className="text-left px-3 py-2 font-medium text-slate-600">Customer</th>
                  <th className="text-right px-3 py-2 font-medium text-slate-600">Qty</th>
                  <th className="text-left px-3 py-2 font-medium text-slate-600">Delivery</th>
                  <th className="text-right px-3 py-2 font-medium text-slate-600">Days Left</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((src, idx) => {
                  const isWoSource = src.source_type === 'work_order';
                  const daysLeft = getDaysRemaining(src.delivery_date);
                  const pendingQty = isWoSource ? src.pending_quantity : src.pending_qty;
                  
                  return (
                    <tr 
                      key={src.line_item_id || `src-${idx}`} 
                      className={`border-t ${isWoSource ? 'bg-purple-50/50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                    >
                      <td className="px-3 py-2 font-medium">
                        {isWoSource ? (
                          <span className="text-purple-600">{src.work_order_number}</span>
                        ) : (
                          src.so_line_code || src.so_number
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {isWoSource ? (
                          <span className="text-purple-500 text-xs">WO BOM: {src.parent_item_name}</span>
                        ) : (
                          src.customer_name || '-'
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-medium">
                        {formatNumber(pendingQty)}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {formatDate(src.delivery_date)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {daysLeft !== null ? (
                          <span className={`font-medium ${daysLeft <= 7 ? 'text-red-600' : daysLeft <= 14 ? 'text-amber-600' : 'text-green-600'}`}>
                            {daysLeft} days
                          </span>
                        ) : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-2 bg-slate-50 border-t text-xs text-slate-500 text-right">
            Sorted by delivery date (most urgent first)
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="production-fulfillment-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Factory className="h-8 w-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Production Fulfillment Register</h1>
            <p className="text-sm text-slate-500">Items with negative Nett Status - Create Work Orders</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchFulfillmentItems}
          disabled={loading}
          data-testid="refresh-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <ClipboardList className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Items</p>
              <p className="text-2xl font-bold text-slate-800">{summary.total_items}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Package className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">FNG Items</p>
              <p className="text-2xl font-bold text-slate-800">{summary.fng_count}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Box className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">SFG Items</p>
              <p className="text-2xl font-bold text-slate-800">{summary.sfg_count}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Link2 className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-purple-600">From WO BOM</p>
              <p className="text-2xl font-bold text-purple-700">{summary.wo_bom_sourced_count || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">BOM Pending</p>
              <p className="text-2xl font-bold text-slate-800">{summary.bom_pending_count}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <TrendingDown className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-red-600">Total Shortage</p>
              <p className="text-2xl font-bold text-red-700">{formatNumber(summary.total_shortage)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by item name, code, or WO number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="production-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Filter:</span>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40" data-testid="category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="SFG">SFG Only</SelectItem>
              <SelectItem value="FNG">FNG Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-sm text-slate-500">
          Sorted by: SFG first, then by earliest delivery date (FIFO)
        </div>
      </div>

      {/* Items List */}
      {(() => {
        const filteredItems = searchTerm
          ? items.filter(item => {
              const term = searchTerm.toLowerCase();
              return (
                item.item_name?.toLowerCase().includes(term) ||
                item.item_code?.toLowerCase().includes(term) ||
                item.linked_so_items?.some(so =>
                  so.work_order_number?.toLowerCase().includes(term) ||
                  so.so_number?.toLowerCase().includes(term)
                )
              );
            })
          : items;

        return loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredItems.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600">{searchTerm ? 'No items match your search' : 'No items need production'}</h3>
            <p className="text-sm text-slate-400 mt-1">{searchTerm ? 'Try a different search term' : 'All items have positive Nett Status - stock is sufficient'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredItems.map((item) => {
            const isExpanded = expandedItem === item.item_id;
            
            return (
              <Card key={item.item_id} className="overflow-hidden" data-testid={`item-card-${item.item_id}`}>
                {/* Item Header */}
                <div 
                  className="p-4 bg-slate-50 border-b cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => toggleItemExpand(item.item_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-800">{item.item_name}</h3>
                          {['SFG', 'FNG'].includes(item.category?.toUpperCase()) && (
                            <Badge variant={item.category?.toUpperCase() === 'SFG' ? 'secondary' : 'default'}>
                              {item.category?.toUpperCase()}
                            </Badge>
                          )}
                          {hasWoBomSource(item) && (
                            <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                              <Link2 className="h-3 w-3 mr-1" />
                              WO BOM
                            </Badge>
                          )}
                          {item.bom_status === 'pending' && (
                            <Badge variant="destructive">BOM Pending</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          {item.item_code} • 
                          <span className="text-red-600 font-medium ml-1">
                            Demand: {formatNumber(item.make_demand || item.total_pending_qty)}
                          </span>
                          <SOBreakdownPopover item={item} />
                          <span className="mx-1">•</span>
                          <span className="text-orange-600 font-medium">
                            Shortage: {formatNumber(item.shortage)}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Nett Status</div>
                        <div className={`text-lg font-bold ${item.nett_status.nett < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatNumber(item.nett_status.nett)}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openCreateWODialog(item);
                        }}
                        disabled={item.bom_status === 'pending'}
                        data-testid={`create-wo-btn-${item.item_id}`}
                      >
                        <Zap className="h-4 w-4 mr-1" />
                        Create WO
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <CardContent className="p-4">
                    {/* Nett Status Table — live recalculated */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-slate-600 mb-2">
                        Nett Status
                        {loadingNettStatus && <Loader2 className="inline h-3 w-3 animate-spin ml-2" />}
                        {liveNettStatus && !loadingNettStatus && (
                          <span className="text-xs font-normal text-green-600 ml-2">Live</span>
                        )}
                      </h4>
                      <NettStatusTable 
                        nettStatus={liveNettStatus || item.nett_status} 
                        shortage={item.shortage} 
                        shortageLabel="production" 
                      />
                    </div>

                    {/* SFG Warning */}
                    {item.sfg_dependencies && item.sfg_dependencies.length > 0 && (
                      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-2 text-amber-700 mb-2">
                          <AlertTriangle className="h-4 w-4" />
                          <span className="font-medium">SFG Dependencies Warning</span>
                        </div>
                        <div className="text-sm text-amber-600">
                          {item.sfg_dependencies.map((dep, idx) => (
                            <div key={idx}>
                              {dep.item_name}: Current Nett {formatNumber(dep.current_nett)}, Need {formatNumber(dep.required)} → Will go negative
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Linked Sources Table - FIFO Order */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-600 mb-2">
                        Demand Sources 
                        <span className="text-xs font-normal text-slate-400 ml-2">(Sorted by delivery date - FIFO)</span>
                      </h4>
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>Source</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Customer/Parent</TableHead>
                            <TableHead>Variant</TableHead>
                            <TableHead className="text-right">Required</TableHead>
                            <TableHead className="text-right">Pending</TableHead>
                            <TableHead>Production</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {item.linked_so_items?.map((src, idx) => {
                            const isWoSource = src.source_type === 'work_order';
                            return (
                              <TableRow key={src.line_item_id || `wo-${idx}`} className={isWoSource ? 'bg-purple-50/50' : ''}>
                                <TableCell className="text-slate-400 text-sm">{idx + 1}</TableCell>
                                <TableCell className="font-medium">
                                  {isWoSource ? src.work_order_number : (src.so_line_code || src.so_number)}
                                </TableCell>
                                <TableCell>
                                  {isWoSource ? (
                                    <Badge className="bg-purple-100 text-purple-700 text-xs">
                                      <Link2 className="h-3 w-3 mr-1" />
                                      WO BOM
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-xs">SO</Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {isWoSource ? (
                                    <span className="text-purple-600">{src.parent_item_name}</span>
                                  ) : (
                                    src.customer_name
                                  )}
                                </TableCell>
                                <TableCell>{src.variant_name || '-'}</TableCell>
                                <TableCell className="text-right">
                                  {formatNumber(isWoSource ? src.required_quantity : src.quantity)}
                                </TableCell>
                                <TableCell className="text-right font-medium text-orange-600">
                                  {formatNumber(isWoSource ? src.pending_quantity : src.pending_qty)}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3 text-slate-400" />
                                    {formatDate(src.target_production_date || src.delivery_date)}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Action Bar */}
                    <div className="mt-4 flex items-center justify-between border-t pt-4">
                      <div className="text-sm text-slate-600">
                        Total SO Demand: <span className="font-semibold">{formatNumber(item.total_pending_qty)} pcs</span>
                        <span className="mx-2">•</span>
                        Shortage (needs production): <span className="font-semibold text-red-600">{formatNumber(item.shortage)} pcs</span>
                      </div>
                      <Button
                        onClick={() => openCreateWODialog(item)}
                        disabled={item.bom_status === 'pending'}
                        data-testid={`create-wo-btn-expanded-${item.item_id}`}
                      >
                        <Factory className="h-4 w-4 mr-2" />
                        Create Work Order
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      );
      })()}

      {/* Create WO Dialog (enhanced component) */}
      <EnhancedCreateWODialog
        open={createWODialogOpen}
        onOpenChange={setCreateWODialogOpen}
        itemId={selectedItem?.item_id}
        itemName={selectedItem?.item_name}
        itemCode={selectedItem?.item_code}
        defaultQty={selectedItem?.shortage}
        maxQty={selectedItem?.shortage}
        isSFG={selectedItem?.category?.toUpperCase() === 'SFG'}
        nettStatus={selectedItem?.nett_status}
        onSuccess={fetchFulfillmentItems}
      >
        {selectedItem && (
          <>
            {/* FIFO Allocation Preview */}
            {fifoPreview.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-600 mb-2">
                  FIFO Allocation Preview
                  <span className="text-xs font-normal text-slate-400 ml-2">(Earliest delivery first)</span>
                </h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-slate-600">Source</th>
                        <th className="text-right px-3 py-2 font-medium text-slate-600">Demand Qty</th>
                        <th className="text-right px-3 py-2 font-medium text-indigo-600">WO Alloc</th>
                        <th className="text-right px-3 py-2 font-medium text-green-600">From Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fifoPreview.map((alloc, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-3 py-2">
                            {alloc.source_type === 'work_order' ? (
                              <>
                                <div className="font-medium text-purple-600">{alloc.work_order_number || 'WO'}</div>
                                <div className="text-xs text-slate-400">For: {alloc.parent_item_name || 'Parent WO'}</div>
                              </>
                            ) : (
                              <>
                                <div className="font-medium">{alloc.so_line_code || alloc.so_number || '-'}</div>
                                <div className="text-xs text-slate-400">{alloc.customer_name || ''}</div>
                              </>
                            )}
                          </td>
                          <td className="text-right px-3 py-2">{formatNumber(alloc.so_qty || 0)}</td>
                          <td className="text-right px-3 py-2 font-semibold text-indigo-600">
                            {formatNumber(alloc.wo_allocated || 0)}
                          </td>
                          <td className="text-right px-3 py-2 font-semibold text-green-600">
                            {(alloc.stock_allocated || 0) > 0 ? formatNumber(alloc.stock_allocated) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t">
                      <tr>
                        <td className="px-3 py-2 font-medium">Total</td>
                        <td className="text-right px-3 py-2 font-medium">
                          {formatNumber(fifoPreview.reduce((sum, a) => sum + (a.so_qty || 0), 0))}
                        </td>
                        <td className="text-right px-3 py-2 font-bold text-indigo-600">
                          {formatNumber(fifoPreview.reduce((sum, a) => sum + (a.wo_allocated || 0), 0))}
                        </td>
                        <td className="text-right px-3 py-2 font-bold text-green-600">
                          {formatNumber(fifoPreview.reduce((sum, a) => sum + (a.stock_allocated || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* SFG Warnings */}
            {selectedItem.sfg_dependencies && selectedItem.sfg_dependencies.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                <div className="flex items-center gap-2 font-medium mb-1">
                  <AlertTriangle className="h-4 w-4" />
                  Warning: SFG items may go negative
                </div>
                <p>Create WO for SFG items from the register after this.</p>
              </div>
            )}
          </>
        )}
      </EnhancedCreateWODialog>
      </div>
    </DashboardLayout>
  );
};

export default ProductionFulfillmentPage;
